import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { heroVariant, staggerContainer, ease } from "@/lib/motion";
import HeroWheel, { WheelSegment } from "./HeroWheel";

export interface HeroContent {
  eyebrow: string;
  headline: string;
  headlineAccent: string;
  description: string;
  diagnosticLine: string;
  executionNote: string;
  primaryCta: string;
  secondaryCta: string;
  secondaryCtaLink: string;
}

export const heroDefaults: HeroContent = {
  eyebrow: "Growth Systems",
  headline: "We build systems that",
  headlineAccent: "compound growth.",
  description: "For companies past quick fixes. Ready for structured, measurable, sustainable scale.",
  diagnosticLine: "If growth has plateaued despite increased spend, the issue is usually structural.",
  executionNote: "Execution begins in parallel once constraints are identified.",
  primaryCta: "Start a serious conversation",
  secondaryCta: "See how it works",
  secondaryCtaLink: "/services",
};

interface HeroProps {
  content?: HeroContent;
}

const Hero = ({ content: c = heroDefaults }: HeroProps) => {
  const shouldReduceMotion = useReducedMotion();
  const [hoveredSegment, setHoveredSegment] = useState<WheelSegment | null>(null);

  const primaryCtaText = hoveredSegment?.ctaText || c.primaryCta;

  return (
    <section className="min-h-[auto] md:min-h-[60vh] lg:min-h-[92vh] flex items-center pt-20 md:pt-24 lg:pt-28 pb-12 md:pb-16 lg:pb-20 relative overflow-hidden bg-background">
      {/* Subtle grid - extremely light */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.018]"
        style={{
          backgroundImage: `
            linear-gradient(hsl(var(--muted-foreground)) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--muted-foreground)) 1px, transparent 1px)
          `,
          backgroundSize: "80px 80px",
        }}
      />

      {/* Soft gradient wash - muted */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 120% 70% at 75% 0%, hsl(var(--primary) / 0.04) 0%, transparent 50%),
            radial-gradient(ellipse 80% 80% at 0% 80%, hsl(var(--muted) / 0.3) 0%, transparent 40%)
          `,
        }}
      />

      <div className="container-wide relative z-10">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16 items-center justify-items-center lg:justify-items-stretch">
          {/* Left: Content */}
          <motion.div
            className="max-w-lg"
            initial={shouldReduceMotion ? "visible" : "hidden"}
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={heroVariant} className="flex items-center gap-3 mb-6">
              <div className="w-6 h-px bg-muted-foreground/40" />
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
                {c.eyebrow}
              </span>
            </motion.div>

            <motion.h1
              variants={heroVariant}
              className="font-display text-[2.5rem] md:text-[3rem] lg:text-[3.25rem] font-semibold leading-[1.08] tracking-tight text-foreground mb-5"
            >
              {c.headline}
              <br />
              <span className="text-primary">{c.headlineAccent}</span>
            </motion.h1>

            <motion.p
              variants={heroVariant}
              className="text-lg text-muted-foreground leading-relaxed mb-6 max-w-md"
            >
              {c.description}
            </motion.p>
            
            <motion.p
              variants={heroVariant}
              className="text-sm text-muted-foreground/70 leading-relaxed mb-2 max-w-md"
            >
              {c.diagnosticLine}
            </motion.p>
            <motion.p
              variants={heroVariant}
              className="text-xs text-muted-foreground/50 leading-relaxed mb-10 max-w-md"
            >
              {c.executionNote}
            </motion.p>

            <motion.div
              variants={heroVariant}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <Button
                variant="default"
                size="lg"
                className="group min-w-[220px] justify-center"
                asChild
              >
                <Link to="/contact">
                  <span className="transition-opacity duration-200">
                    {primaryCtaText}
                  </span>
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Link
                to={c.secondaryCtaLink}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-1"
              >
                {c.secondaryCta}
                <span className="ml-1 opacity-60">→</span>
              </Link>
            </motion.div>
          </motion.div>

          {/* Right: Interactive Growth System */}
          <motion.div
            className="relative hidden lg:flex items-center justify-center lg:justify-end"
            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.6, ease }}
          >
            <HeroWheel onSegmentHover={setHoveredSegment} />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
